import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { planService } from "@/api/services"
import type { PlanResponseDto, PlanTarget } from "@/api/types/plan.types"
import Sidebar from "@/components/Sidebar"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import FileUpload from "@/components/FileUpload"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2, ArrowLeft, Edit2, Trash2, Save } from "lucide-react"

export default function PlanDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [plan, setPlan] = useState<PlanResponseDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Edit states
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    target: "Basic" as PlanTarget,
    creditAmount: "",
    adQuotaMotoCount: "",
    adQuotaGearCount: "",
    adQuotaPeriodDays: "",
    priceRsd: "",
    backgroundColor: "",
    iconUploadedFileId: "",
    isActive: true,
    sequence: "",
  })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  useEffect(() => {
    if (id) {
      loadPlan()
    }
  }, [id])

  const loadPlan = async () => {
    if (!id) return
    setLoading(true)
    setError("")
    try {
      const data = await planService.getPlanByIdAdmin(id)
      setPlan(data)
      setFormData({
        name: data.name,
        description: data.description,
        target: data.target,
        creditAmount: data.creditAmount?.toString() || "",
        adQuotaMotoCount: data.adQuotaMotoCount?.toString() || "",
        adQuotaGearCount: data.adQuotaGearCount?.toString() || "",
        adQuotaPeriodDays: data.adQuotaPeriodDays?.toString() || "",
        priceRsd: data.priceRsd || "",
        backgroundColor: data.backgroundColor || "#FF5733",
        iconUploadedFileId: data.iconUploadedFileId || "",
        isActive: data.isActive,
        sequence: data.sequence?.toString() || "",
      })
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load plan"
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    if (plan) {
      setFormData({
        name: plan.name,
        description: plan.description,
        target: plan.target,
        creditAmount: plan.creditAmount?.toString() || "",
        adQuotaMotoCount: plan.adQuotaMotoCount?.toString() || "",
        adQuotaGearCount: plan.adQuotaGearCount?.toString() || "",
        adQuotaPeriodDays: plan.adQuotaPeriodDays?.toString() || "",
        priceRsd: plan.priceRsd || "",
        backgroundColor: plan.backgroundColor || "#FF5733",
        iconUploadedFileId: plan.iconUploadedFileId || "",
        isActive: plan.isActive,
        sequence: plan.sequence?.toString() || "",
      })
    }
  }

  const handleSave = async () => {
    if (!id) return

    if (!formData.name.trim()) {
      setError("Name is required")
      return
    }

    if (!formData.description.trim()) {
      setError("Description is required")
      return
    }

    if (
      !formData.creditAmount ||
      isNaN(Number(formData.creditAmount)) ||
      Number(formData.creditAmount) < 0
    ) {
      setError("Valid credit amount is required")
      return
    }

    if (formData.target === "Company") {
      if (
        !formData.adQuotaMotoCount ||
        isNaN(Number(formData.adQuotaMotoCount)) ||
        Number(formData.adQuotaMotoCount) < 0
      ) {
        setError("Valid ad quota moto count is required")
        return
      }

      if (
        !formData.adQuotaGearCount ||
        isNaN(Number(formData.adQuotaGearCount)) ||
        Number(formData.adQuotaGearCount) < 0
      ) {
        setError("Valid ad quota gear count is required")
        return
      }

      if (
        !formData.adQuotaPeriodDays ||
        isNaN(Number(formData.adQuotaPeriodDays)) ||
        Number(formData.adQuotaPeriodDays) < 0
      ) {
        setError("Valid ad quota period days is required")
        return
      }
    }

    if (
      !formData.priceRsd ||
      isNaN(Number(formData.priceRsd)) ||
      Number(formData.priceRsd) < 0
    ) {
      setError("Valid price (RSD) is required")
      return
    }

    if (
      !formData.sequence ||
      isNaN(Number(formData.sequence)) ||
      Number(formData.sequence) < 0
    ) {
      setError("Valid sequence is required")
      return
    }

    setSaving(true)
    setError("")
    setSuccess("")

    try {
      const updated = await planService.updatePlan(id, {
        name: formData.name.trim(),
        description: formData.description.trim(),
        target: formData.target,
        creditAmount: Number(formData.creditAmount),
        adQuotaMotoCount: formData.adQuotaMotoCount ? Number(formData.adQuotaMotoCount) : undefined,
        adQuotaGearCount: formData.adQuotaGearCount ? Number(formData.adQuotaGearCount) : undefined,
        adQuotaPeriodDays: formData.adQuotaPeriodDays ? Number(formData.adQuotaPeriodDays) : undefined,
        priceRsd: Number(formData.priceRsd),
        backgroundColor: formData.backgroundColor,
        iconUploadedFileId: formData.iconUploadedFileId.trim() || null,
        isActive: formData.isActive,
        sequence: Number(formData.sequence),
      })
      setPlan(updated)
      setSuccess("Plan updated successfully")
      setIsEditing(false)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update plan"
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    setDeleting(true)
    setError("")
    setDeleteDialogOpen(false)

    try {
      await planService.deletePlan(id)
      navigate("/plans")
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to delete plan"
      setError(errorMessage)
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    )
  }

  if (!plan) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
          <div className="max-w-2xl">
            <Card className="p-6">
              <div className="text-center">
                <p className="text-destructive mb-4">{error || "Plan not found"}</p>
                <Button onClick={() => navigate("/plans")} variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Plans
                </Button>
              </div>
            </Card>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex">
      <Sidebar />

      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="sm" onClick={() => navigate("/plans")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Plans
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Plan Details</h1>
          </div>

          {/* Success message */}
          {success && (
            <div className="bg-success/10 border border-success/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Plan Information */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Plan Information</h2>
              {!isEditing && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    <Edit2 className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={deleting}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="target">
                      Target <span className="text-destructive">*</span>
                    </Label>
                    <select
                      id="target"
                      value={formData.target}
                      onChange={(e) =>
                        setFormData({ ...formData, target: e.target.value as PlanTarget })
                      }
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="Basic">Basic</option>
                      <option value="Company">Company</option>
                      <option value="BasicAndCompany">Basic and Company</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">
                    Description <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && handleSave()}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="priceRsd">
                      Price (RSD) <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="priceRsd"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.priceRsd}
                      onChange={(e) => setFormData({ ...formData, priceRsd: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="creditAmount">
                      Credit Amount <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="creditAmount"
                      type="number"
                      step="1"
                      min="0"
                      value={formData.creditAmount}
                      onChange={(e) =>
                        setFormData({ ...formData, creditAmount: e.target.value })
                      }
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="adQuotaMotoCount">
                      Ad Quota Moto Count {formData.target === "Company" && <span className="text-destructive">*</span>}
                    </Label>
                    <Input
                      id="adQuotaMotoCount"
                      type="number"
                      step="1"
                      min="0"
                      value={formData.adQuotaMotoCount}
                      onChange={(e) =>
                        setFormData({ ...formData, adQuotaMotoCount: e.target.value })
                      }
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="adQuotaGearCount">
                      Ad Quota Gear Count {formData.target === "Company" && <span className="text-destructive">*</span>}
                    </Label>
                    <Input
                      id="adQuotaGearCount"
                      type="number"
                      step="1"
                      min="0"
                      value={formData.adQuotaGearCount}
                      onChange={(e) =>
                        setFormData({ ...formData, adQuotaGearCount: e.target.value })
                      }
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="adQuotaPeriodDays">
                      Ad Quota Period (Days) {formData.target === "Company" && <span className="text-destructive">*</span>}
                    </Label>
                    <Input
                      id="adQuotaPeriodDays"
                      type="number"
                      step="1"
                      min="0"
                      value={formData.adQuotaPeriodDays}
                      onChange={(e) =>
                        setFormData({ ...formData, adQuotaPeriodDays: e.target.value })
                      }
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sequence">
                      Sequence <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="sequence"
                      type="number"
                      step="1"
                      min="0"
                      value={formData.sequence}
                      onChange={(e) => setFormData({ ...formData, sequence: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="backgroundColor">
                      Background Color <span className="text-destructive">*</span>
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="backgroundColor"
                        type="color"
                        value={formData.backgroundColor}
                        onChange={(e) =>
                          setFormData({ ...formData, backgroundColor: e.target.value })
                        }
                        className="h-10 w-20"
                      />
                      <Input
                        type="text"
                        value={formData.backgroundColor}
                        onChange={(e) =>
                          setFormData({ ...formData, backgroundColor: e.target.value })
                        }
                        placeholder="#FF5733"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <FileUpload
                    label="Icon"
                    description="Upload an icon for this plan"
                    value={formData.iconUploadedFileId || undefined}
                    onChange={(fileId) =>
                      setFormData({ ...formData, iconUploadedFileId: fileId || "" })
                    }
                    onDeleteSuccess={async () => {
                      if (id && isEditing && plan) {
                        try {
                          await planService.updatePlan(id, {
                            name: plan.name,
                            description: plan.description,
                            target: plan.target,
                            creditAmount: plan.creditAmount,
                            adQuotaMotoCount: plan.adQuotaMotoCount,
                            adQuotaGearCount: plan.adQuotaGearCount,
                            adQuotaPeriodDays: plan.adQuotaPeriodDays,
                            priceRsd: Number(plan.priceRsd),
                            backgroundColor: plan.backgroundColor,
                            iconUploadedFileId: null,
                            isActive: plan.isActive,
                            sequence: plan.sequence,
                          })
                          setFormData({ ...formData, iconUploadedFileId: "" })
                          await loadPlan()
                        } catch (err: any) {
                          const errorMessage =
                            err.response?.data?.message ||
                            err.message ||
                            "Failed to update plan"
                          setError(errorMessage)
                        }
                      } else {
                        setFormData({ ...formData, iconUploadedFileId: "" })
                      }
                    }}
                    previewSize="md"
                    disabled={saving}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="isActive" className="cursor-pointer">
                      Active
                    </Label>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Name</Label>
                    <p className="text-foreground font-medium">{plan.name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Target</Label>
                    <div className="mt-1">
                      <Badge variant="outline">{plan.target}</Badge>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Description</Label>
                    <p className="text-foreground font-medium">{plan.description}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Price (RSD)</Label>
                    <p className="text-foreground font-medium">{plan.priceRsd || "N/A"} RSD</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Credit Amount</Label>
                    <p className="text-foreground font-medium">{plan.creditAmount ?? "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Ad Quota Moto Count</Label>
                    <p className="text-foreground font-medium">{plan.adQuotaMotoCount ?? "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Ad Quota Gear Count</Label>
                    <p className="text-foreground font-medium">{plan.adQuotaGearCount ?? "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Ad Quota Period (Days)</Label>
                    <p className="text-foreground font-medium">{plan.adQuotaPeriodDays ?? "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Sequence</Label>
                    <p className="text-foreground font-medium">{plan.sequence ?? "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Background Color</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded border"
                        style={{ backgroundColor: plan.backgroundColor || "#FF5733" }}
                      />
                      <p className="text-foreground font-medium">{plan.backgroundColor || "#FF5733"}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Status</Label>
                    <div className="mt-1">
                      <Badge variant={plan.isActive ? "default" : "secondary"}>
                        {plan.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                  {plan.iconUploadedFile && (
                    <div className="col-span-2">
                      <Label className="text-muted-foreground">Icon</Label>
                      <div className="mt-2">
                        <img
                          src={plan.iconUploadedFile.url}
                          alt={plan.iconUploadedFile.name}
                          className="h-16 w-16 object-contain border rounded"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {plan.iconUploadedFile.name}
                        </p>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-muted-foreground">Created At</Label>
                    <p className="text-foreground font-medium">
                      {new Date(plan.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Updated At</Label>
                    <p className="text-foreground font-medium">
                      {new Date(plan.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </main>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this plan? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

